import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * Fake macOS terminal that live-types a small "about me" program in a loop,
 * token by token with syntax colouring.
 */
const Shell = styled.div`
  border: 1px solid var(--border-strong);
  border-radius: 16px;
  background: var(--elev-bg);
  box-shadow: var(--card-shadow);
  overflow: hidden;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.84rem;
  line-height: 1.75;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  span.dot { width: 11px; height: 11px; border-radius: 50%; }
  .red { background: #ff5f57; }
  .yellow { background: #febc2e; }
  .green { background: #28c840; }
  .title {
    margin-left: 0.5rem;
    font-size: 0.72rem;
    color: var(--text-faint);
    letter-spacing: 0.04em;
  }
`;

const Screen = styled.pre`
  margin: 0;
  padding: 1rem 1.1rem 1.2rem;
  min-height: ${(p) => p.$lines * 1.75}em;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;

  .kw { color: #c084fc; }
  .fn { color: #818cf8; }
  .str { color: #34d399; }
  .num { color: #f472b6; }
  .cmt { color: var(--text-faint); font-style: italic; }
  .pln { color: var(--text); }
  .op { color: var(--text-muted); }

  .cursor {
    display: inline-block;
    width: 0.55em;
    height: 1.1em;
    margin-left: 1px;
    vertical-align: text-bottom;
    background: var(--primary-light);
    animation: blink 1s steps(1) infinite;
  }
  @keyframes blink { 50% { opacity: 0; } }
`;

// [text, cssClass] tokens per line.
const LINES = [
  [['const ', 'kw'], ['dev', 'pln'], [' = ', 'op'], ['new ', 'kw'], ['FullStackDev', 'fn'], ['(', 'op'], ["'Simon Saifi'", 'str'], [')', 'op'], [';', 'op']],
  [['dev', 'pln'], ['.', 'op'], ['stack', 'pln'], [' = [', 'op'], ["'React'", 'str'], [', ', 'op'], ["'Spring Boot'", 'str'], [', ', 'op'], ["'PHP'", 'str'], [', ', 'op'], ["'Flutter'", 'str'], ['];', 'op']],
  [['dev', 'pln'], ['.', 'op'], ['base', 'pln'], [' = ', 'op'], ["'Marseille, FR'", 'str'], [';', 'op']],
  [['dev', 'pln'], ['.', 'op'], ['currently', 'fn'], ['(', 'op'], ["'CCAS de Marseille'", 'str'], [', { ', 'op'], ['users', 'pln'], [': ', 'op'], ['350', 'num'], [' })', 'op'], [';', 'op']],
  [['dev', 'pln'], ['.', 'op'], ['openTo', 'fn'], ['(', 'op'], ["'Full-Stack CDI'", 'str'], [')', 'op'], [';', 'op']],
  [['await ', 'kw'], ['dev', 'pln'], ['.', 'op'], ['ship', 'fn'], ['()', 'op'], [';', 'op'], [' // ✓ deployed to production', 'cmt']],
];

const TYPE_MS = 26;
const RESTART_MS = 4200;

const CodeTerminal = () => {
  // pos = { line, token, char }
  const [pos, setPos] = useState({ line: 0, token: 0, char: 0 });
  const timer = useRef(null);

  useEffect(() => {
    const tick = () => {
      setPos((p) => {
        const line = LINES[p.line];
        if (!line) return p;
        const tok = line[p.token];
        if (p.char < tok[0].length) return { ...p, char: p.char + 1 };
        if (p.token < line.length - 1) return { ...p, token: p.token + 1, char: 1 };
        return { line: p.line + 1, token: 0, char: 0 };
      });
    };
    timer.current = setInterval(tick, TYPE_MS);
    return () => clearInterval(timer.current);
  }, []);

  const done = pos.line >= LINES.length;

  useEffect(() => {
    if (!done) return undefined;
    clearInterval(timer.current);
    const t = setTimeout(() => {
      setPos({ line: 0, token: 0, char: 0 });
      timer.current = setInterval(() => {
        setPos((p) => {
          const line = LINES[p.line];
          if (!line) return p;
          const tok = line[p.token];
          if (p.char < tok[0].length) return { ...p, char: p.char + 1 };
          if (p.token < line.length - 1) return { ...p, token: p.token + 1, char: 1 };
          return { line: p.line + 1, token: 0, char: 0 };
        });
      }, TYPE_MS);
    }, RESTART_MS);
    return () => clearTimeout(t);
  }, [done]);

  const rendered = [];
  for (let li = 0; li < LINES.length; li++) {
    if (li > pos.line) break;
    const parts = [];
    const line = LINES[li];
    for (let ti = 0; ti < line.length; ti++) {
      const [text, cls] = line[ti];
      if (li < pos.line) {
        parts.push(<span key={ti} className={cls}>{text}</span>);
      } else if (ti < pos.token) {
        parts.push(<span key={ti} className={cls}>{text}</span>);
      } else if (ti === pos.token) {
        parts.push(<span key={ti} className={cls}>{text.slice(0, pos.char)}</span>);
        break;
      }
    }
    rendered.push(
      <div key={li}>
        {parts}
        {li === pos.line && <span className="cursor" />}
      </div>
    );
  }
  if (done) rendered.push(<div key="end"><span className="cursor" /></div>);

  return (
    <Shell aria-hidden="true">
      <TitleBar>
        <span className="dot red" />
        <span className="dot yellow" />
        <span className="dot green" />
        <span className="title">simon@marseille — zsh</span>
      </TitleBar>
      <Screen $lines={LINES.length + 1}>{rendered}</Screen>
    </Shell>
  );
};

export default CodeTerminal;
